import { fold } from './fold.ts'
import type { Concert } from './program-schema.ts'

/**
 * Free-text artist search across the whole programme.
 *
 * Pure and snapshot-only: the caller hands in `CONCERTS`, which is bundled, so
 * search answers "when is X playing" with no network and no sign-in — the two things
 * a festival field most reliably takes away.
 *
 * Name folding lives in `domain/fold.ts`, shared with the Broadcast import. Both
 * resolve a name nobody typed carefully against the programme — one from a query, one
 * from a pasted schedule — and two folders that disagreed would produce a bug that
 * reads as two unrelated ones.
 */

/**
 * Concerts whose artist name contains `query`, best match first.
 *
 * Substring rather than prefix-only matching, because half the lineup is a phrase —
 * `Jazznytt: om Reolô og Hayeminol` is found by typing `reolo`, which is what someone
 * who knows the artist but not the billing will actually do. Prefix matches still
 * sort first: when the typed letters open the name, that is almost always the artist
 * being looked for. Everything after that is programme order, day then start time, so
 * two matches read the way the schedule does.
 *
 * The cap keeps the list under the fold on a phone. It is applied last, after the
 * ranking, so the eight shown are the eight best rather than the first eight found.
 */
export function searchConcerts(
  concerts: readonly Concert[],
  query: string,
  limit = 8,
): Concert[] {
  const needle = fold(query)
  if (needle === '') return []

  const hits: { concert: Concert; prefix: boolean }[] = []
  for (const concert of concerts) {
    const at = fold(concert.name).indexOf(needle)
    if (at === -1) continue
    hits.push({ concert, prefix: at === 0 })
  }

  hits.sort((a, b) => {
    if (a.prefix !== b.prefix) return a.prefix ? -1 : 1
    return (
      a.concert.day.localeCompare(b.concert.day) ||
      a.concert.start.localeCompare(b.concert.start) ||
      a.concert.name.localeCompare(b.concert.name, 'nb')
    )
  })

  return hits.slice(0, limit).map((hit) => hit.concert)
}
